import { clampNormRect } from "../editor/coords.js";
import { rectsOverlap, rectInside } from "./guides.js";
export function detectLayerIssues(slide, theme) {
    const issues = [];
    const safe = theme.legibility.safeZones[0];
    const textLayers = slide.layers.filter((l) => l.type === "text");
    for (const layer of textLayers) {
        if (safe && !rectInside(layer, safe)) {
            issues.push({
                layerId: layer.id,
                kind: "outside-safezone",
                message: "Testo fuori dalla safe zone — sposta o aggiungi scrim.",
            });
        }
        for (const other of slide.layers) {
            if (other.id === layer.id || other.type === "shape")
                continue;
            if (rectsOverlap(layer, other)) {
                issues.push({
                    layerId: layer.id,
                    kind: "overlap",
                    otherLayerId: other.id,
                    message: "Sovrapposizione con un altro layer.",
                });
            }
        }
    }
    return issues;
}
function nudgeAway(a, b) {
    const dx = a.x + a.w / 2 < b.x + b.w / 2 ? -0.03 : 0.03;
    const dy = a.y + a.h / 2 < b.y + b.h / 2 ? -0.03 : 0.03;
    return clampNormRect({ x: a.x + dx, y: a.y + dy, w: a.w, h: a.h });
}
/** Repack text layers inside primary safeZone; resolve pairwise overlaps. */
export function arrangeTextInSafeZones(slide, theme) {
    const safe = theme.legibility.safeZones[0] ?? { x: 0.08, y: 0.12, w: 0.84, h: 0.76 };
    let layers = slide.layers.map((l) => ({ ...l }));
    const textIndices = layers
        .map((l, i) => ({ l, i }))
        .filter(({ l }) => l.type === "text")
        .sort((a, b) => a.l.y - b.l.y || a.l.z - b.l.z);
    let yCursor = safe.y;
    const gap = 0.02;
    for (const { i } of textIndices) {
        const layer = layers[i];
        const h = Math.min(layer.h, safe.h * 0.35);
        layers[i] = {
            ...layer,
            ...clampNormRect({
                x: Math.max(safe.x, Math.min(layer.x, safe.x + safe.w - layer.w)),
                y: Math.min(yCursor, safe.y + safe.h - h),
                w: Math.min(layer.w, safe.w),
                h,
            }),
        };
        yCursor = layers[i].y + layers[i].h + gap;
    }
    for (let pass = 0; pass < 6; pass += 1) {
        let moved = false;
        for (let i = 0; i < layers.length; i += 1) {
            const a = layers[i];
            if (a.type !== "text")
                continue;
            for (let j = 0; j < layers.length; j += 1) {
                if (i === j)
                    continue;
                const b = layers[j];
                if (rectsOverlap(a, b)) {
                    const nudged = nudgeAway(a, b);
                    layers[i] = { ...a, ...nudged };
                    moved = true;
                }
            }
        }
        if (!moved)
            break;
    }
    return layers;
}
export function alignLayers(layers, ids, mode) {
    const selected = layers.filter((l) => ids.includes(l.id));
    if (selected.length < 2)
        return layers;
    const minX = Math.min(...selected.map((l) => l.x));
    const maxX = Math.max(...selected.map((l) => l.x + l.w));
    const minY = Math.min(...selected.map((l) => l.y));
    const maxY = Math.max(...selected.map((l) => l.y + l.h));
    const midX = (minX + maxX) / 2;
    const midY = (minY + maxY) / 2;
    return layers.map((layer) => {
        if (!ids.includes(layer.id))
            return layer;
        let { x, y } = layer;
        if (mode === "left")
            x = minX;
        if (mode === "right")
            x = maxX - layer.w;
        if (mode === "center-h")
            x = midX - layer.w / 2;
        if (mode === "top")
            y = minY;
        if (mode === "bottom")
            y = maxY - layer.h;
        if (mode === "middle-v")
            y = midY - layer.h / 2;
        return { ...layer, ...clampNormRect({ x, y, w: layer.w, h: layer.h }) };
    });
}
export function distributeLayers(layers, ids, axis) {
    const selected = layers.filter((l) => ids.includes(l.id)).sort((a, b) => (axis === "horizontal" ? a.x - b.x : a.y - b.y));
    if (selected.length < 3)
        return layers;
    const first = selected[0];
    const last = selected[selected.length - 1];
    const span = axis === "horizontal"
        ? last.x + last.w - first.x
        : last.y + last.h - first.y;
    const totalSize = selected.reduce((s, l) => s + (axis === "horizontal" ? l.w : l.h), 0);
    const gap = (span - totalSize) / (selected.length - 1);
    let cursor = axis === "horizontal" ? first.x : first.y;
    const positions = new Map();
    for (const layer of selected) {
        positions.set(layer.id, {
            x: axis === "horizontal" ? cursor : layer.x,
            y: axis === "vertical" ? cursor : layer.y,
        });
        cursor += (axis === "horizontal" ? layer.w : layer.h) + gap;
    }
    return layers.map((layer) => {
        const pos = positions.get(layer.id);
        if (!pos)
            return layer;
        return { ...layer, ...clampNormRect({ x: pos.x, y: pos.y, w: layer.w, h: layer.h }) };
    });
}
export function fitLayersToSafeZone(layers, ids, safe) {
    return layers.map((layer) => {
        if (!ids.includes(layer.id) || layer.type !== "text")
            return layer;
        return {
            ...layer,
            ...clampNormRect({
                x: Math.max(safe.x, Math.min(layer.x, safe.x + safe.w - layer.w)),
                y: Math.max(safe.y, Math.min(layer.y, safe.y + safe.h - layer.h)),
                w: Math.min(layer.w, safe.w),
                h: Math.min(layer.h, safe.h),
            }),
        };
    });
}
export function arrangeSlideText(slide, theme) {
    return { ...slide, layers: arrangeTextInSafeZones(slide, theme) };
}
